import { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Typography from "@mui/material/Typography";
import { colors } from "../../utils/const";
import en from "../../../locales/common/en";
import { useI18n } from "@/i18n/I18nProvider";

const steps = ["", "", "", "", ""];

const DEFAULT_HEIGHT = { xs: "200px", sm: "500px", md: "600px", lg: "700px" };

// added prop: heightSx
export default function HorizontalNonLinearStepper({
  heightSx,
  stepsFeature = [],
} = {}) {
  const { t, locale } = useI18n();
  const stepsList = stepsFeature || [];
  const [activeStep, setActiveStep] = useState(0);

  const handleStep = (step) => () => setActiveStep(step);

  const autoIncrementStep = useCallback(() => {
    setActiveStep((prev) => (prev === stepsList.length - 1 ? 0 : prev + 1));
  }, []);

  useEffect(() => {
    const interval = setInterval(autoIncrementStep, 3000);
    return () => clearInterval(interval);
  }, [autoIncrementStep]);

  const getMargins = (idx) => {
    const margins = [
      { mb: { xs: 36, md: 40 }, mt: { xs: 4, sm: 0, lg: 5 } },
      { mb: { xs: 26, md: 28 }, mt: { xs: 14, sm: 10, md: 12, lg: 17 } },
      { mb: { xs: 16, md: 18 }, mt: { xs: 24, sm: 20, md: 22, lg: 27 } },
      {
        mb: { xs: 6, md: 8 },
        mt:
          locale === "en"
            ? { xs: 31, sm: 30, md: 32, lg: 37 }
            : { xs: 34, sm: 30, md: 32, lg: 37 },
      },
      { mb: -2, mt: { xs: 42, sm: 38, md: 42, lg: 47 } },
    ];
    return margins[idx] || {};
  };

  const contentGen = useCallback(
    ({ heading, text, img, idx ,titleVariant}) => (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start" },
          color: "white",
          mx: { xs: 2, md: 4 },
          zIndex: 999999,
        }}
        id={`step-content-${idx}`}
      >
        <Box
          data-aos="fade-up"
          data-aos-delay="100"
          sx={{ width: { xs: "100%", sm: "100%", md: "80%", lg: "90%" } }}
        >
          <Box sx={getMargins(idx)}>
            <Typography
              variant={titleVariant}
              sx={{
                fontSize: { xs: "20px", md: "25px", lg: "30px" },
                mb: { md: 1 },
              }}
            >
              {heading}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "10px", sm: "12px", md: "16px" },
              }}
            >
              {text}
            </Typography>
          </Box>

          <Box
            sx={{
              mr: locale === "ar" ? { xs: -6, md: -8 } : undefined,
              ml: locale === "en" ? { xs: -6, md: -8 } : undefined,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Box sx={{ mb: 2, mt: 7 }}>
                <a
                  href="https://play.google.com/store/apps/details?id=eg.contact"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Box
                    component="img"
                    sx={{
                      width: { xs: "80px", lg: "140px" },
                      height: { xs: "24px", lg: "40px" },
                      cursor: "pointer",
                      mt: 1,
                    }}
                    src={
                      t === en
                        ? "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/home-google-play.webp"
                        : "/googleplay-ar.svg"
                    }
                    alt="google-play"
                    loading="lazy"
                  />
                </a>
                <a
                  href="https://apps.apple.com/us/app/contactnow-%D9%83%D9%88%D9%86%D8%AA%D9%83%D8%AA/id1544159088"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Box
                    component="img"
                    sx={{
                      width: { xs: "80px", lg: "140px" },
                      height: { xs: "25px", lg: "40px" },
                      cursor: "pointer",
                      mx: { xs: 1, md: 2 },
                      mt: 1,
                    }}
                    src={
                      t === en
                        ? "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/home-app-store.webp"
                        : "/appstore-ar.svg"
                    }
                    alt="app-store"
                    loading="lazy"
                  />
                </a>
              </Box>

              <Box>
                <Box
                  component="img"
                  sx={{
                    width: { xs: "50px", md: "100px" },
                    height: { xs: "50px", md: "100px" },
                  }}
                  src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/qr-contactNow.webp"
                  loading="lazy"
                  alt="qrcode"
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          component="img"
          sx={{
            m: { md: "inherit", xs: "auto" },
            width: { xs: "200px", xl: "300px" },
            height: { sm: "400px", xl: "600px" },
            display: { xs: "none", sm: "block" },
          }}
          src={img}
          alt={`step-img-${idx}`}
          loading="lazy"
        />
      </Box>
    ),
    [locale, t],
  );

  return (
    <Box
      className="stepper-box"
      sx={{
        backgroundColor: colors.blue,
        py: { xs: 35, sm: 20 },
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        gap: 6,
        position: "relative",
        zIndex: 1,

        // was fixed sizes — now overridable to match Cover height
        height: heightSx ?? DEFAULT_HEIGHT,
      }}
    >
      <Box
        sx={{
          right: { xs: 0, md: locale === "en" ? 0 : "10%" },
          left: { xs: 0, md: locale === "en" ? "10%" : 0 },
          top: { xs: "5%", md: "20%" },
          display: "flex",
          mt: { xs: -30, sm: 0 },
          px: { xs: 2, md: 8 },
          zIndex: 999,
          width: "100%",
        }}
      >
        <Box
          alt="contact now logo"
          component="img"
          src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/contact-now.webp"
          sx={{
            width: { xs: "60px", lg: "80px" },
            height: { xs: "35px", lg: "45px" },
            display: { xs: "block", sm: "none", lg: "block" },
            mt: 3,
            mb: 5,
            position: "absolute",
            top: "2px",
          }}
        />

        <Stepper
          sx={{ display: "block", zIndex: 999999, pt: { xs: 4, lg: 6 } }}
          nonLinear
          activeStep={activeStep}
          orientation="vertical"
        >
          {stepsList.map((_, index) => (
            <Step key={index}>
              <StepButton
                color="inherit"
                onClick={handleStep(index)}
                sx={{
                  py: { xs: 0.5, md: 1 },
                  display: "flex",
                  justifyContent: "center",
                }}
                aria-label={`step ${index}`}
              />
            </Step>
          ))}
        </Stepper>

        {/* Load all steps once and show/hide based on active step */}
        {stepsFeature.map((stepData, idx) => (
          <Box
            key={idx}
            sx={{
              display: activeStep === idx ? "flex" : "none",
              width: "100%",
            }}
          >
            {contentGen({
              heading:
                locale === "en"
                  ? stepData.title.text.en
                  : stepData.title.text.ar,

              text:
                locale === "en"
                  ? stepData.description.text.en
                  : stepData.description.text.ar,

              img: locale === "en" ? stepData.image.en : stepData.image.ar,
                titleVariant: stepData.title.type,

              idx,
            })}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(https://image-solution-no-scale.s3.us-east-2.amazonaws.com/upload/home-line.png)`,
          backgroundRepeat: "no-repeat",
          transform: `scaleX(${locale === "en" ? -1 : 1})`,
          zIndex: 1,
        }}
      />
    </Box>
  );
}
